import React, { useMemo } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CityAutocomplete from "../CityAutocomplete";

export default function TrechoContentSection({
  companies = [],
  companiesLoading = false,
  formData,
  handleProjectChange,
  categories,
  categoriesLoading = false,
  subcats,
  handleText,
  handleCategoryChange,
  handleSubcatChange,
  origemCity,
  destinoCity,
  onOrigemChange,
  onDestinoChange,
}) {
  const { t } = useTranslation("products");

  const tagsValue = useMemo(() => {
    const raw = formData.tags || "";
    return String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [formData.tags]);

  const handleTagsChange = (_event, newValue) => {
    const str = (newValue || [])
      .map((v) => (typeof v === "string" ? v : v?.label || ""))
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    handleText({ target: { name: "tags", value: str } });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={t("trechoForm.contentTitle")}
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={companies}
              getOptionLabel={(o) => o?.nome || o?.name || ""}
              value={
                companies.find((c) => String(c.id) === String(formData.ids_projetos)) || null
              }
              onChange={handleProjectChange}
              loading={companiesLoading}
              loadingText={t("form.loadingCompanies")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.companyRequired")}
                  placeholder={t("form.selectCompany")}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CityAutocomplete
              label={t("trechoForm.originRequired")}
              value={origemCity}
              onChange={onOrigemChange}
              required
              excludeId={destinoCity?.id}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CityAutocomplete
              label={t("trechoForm.destinationRequired")}
              value={destinoCity}
              onChange={onDestinoChange}
              required
              excludeId={origemCity?.id}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t("trechoForm.nameRequired")}
              name="nome"
              value={formData.nome}
              onChange={handleText}
              fullWidth
              required
              placeholder={t("trechoForm.namePlaceholder")}
              helperText={t("trechoForm.nameHelper")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={handleText}
              fullWidth
              placeholder={t("trechoForm.skuPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t("trechoForm.sourceLabel")}
              name="fonte"
              value={formData.fonte || ""}
              onChange={handleText}
              fullWidth
              placeholder={t("trechoForm.sourcePlaceholder")}
              helperText={t("trechoForm.sourceHelper")}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={tagsValue}
              onChange={handleTagsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.tagsLabel")}
                  placeholder={t("form.tagsPlaceholder")}
                  helperText={t("trechoForm.tagsHelper")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={categories}
              getOptionLabel={(o) => o?.nome || ""}
              value={
                categories.find((c) => String(c.id) === String(formData.id_categoria)) || null
              }
              onChange={handleCategoryChange}
              loading={categoriesLoading}
              loadingText={t("form.loadingCategories")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.categoryLabel")}
                  placeholder={t("form.selectEllipsis")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={subcats}
              getOptionLabel={(o) => o?.nome || ""}
              value={
                subcats.find((s) => String(s.id) === String(formData.id_subcategoria)) || null
              }
              onChange={handleSubcatChange}
              disabled={!subcats.length}
              loading={categoriesLoading}
              loadingText={t("form.loadingSubcategories")}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("form.subcategoryLabel")}
                  placeholder={
                    subcats.length ? t("form.selectEllipsis") : t("form.subcategoryNone")
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
